import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import fetcher from "../../../../dataProvider";
import {
  setLoading,
  setToastData,
} from "../../../../redux/slices/customerJourney";
import { setExamRulesData } from "../../../../redux/slices/examDetails";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { stepCancelProps } from "../../../../types/common";
import { getCountryId, getLanguageCode } from "../../../../utils/common";
import OrderCard from "../../../common/Card";
import Popup from "../../../common/Modal";
import styles from "./styles";
import usePageView from "../../../../utils/analytics/usePageView";
import GTM from "../../../../utils/analytics/gtm";
import { NoSlotsChecker } from "../../../common/NoSlotsChecker";

const TermsAndCondition = (props: stepCancelProps) => {
  usePageView("Terms & Conditions");
  const customerJourney = useAppSelector((state) => state.customerJourney);
  const examDataStore = useAppSelector((state) => state.examDetails);
  const { countryList, pageText } = customerJourney;
  const { examRulesData } = examDataStore;
  const { toggleSteps } = props;
  const [open, setOpen] = useState(false);
  const [examDetails, setExamDetails] = useState<any>(null);
  const [acceptTerms, setAcceptTerms] = useState<any>("");
  const dispatch = useAppDispatch();

  const getExamRules = async () => {
    dispatch(setLoading({ loading: true }));
    try {
      let examData: any = sessionStorage.getItem("examDetails");
      examData = JSON.parse(examData);
      const countryId = getCountryId(countryList);
      let languageCode = getLanguageCode();
      if (languageCode && countryId) {
        const { data } = await fetcher.get(
          `/api/Exam/?languageCode=${languageCode}&id=${examData.examId}&alias=${countryId?.textValue}`
        );
        dispatch(setExamRulesData({ details: data }));
        sessionStorage.setItem("examRulesData", JSON.stringify(data));
      }
      dispatch(setLoading({ loading: false }));
    } catch (e) {
      console.log("Error ", e);
      dispatch(setLoading({ loading: false }));
      dispatch(
        setToastData({
          toastData: {
            message: pageText?.labelsMetadata?.somethingwrong,
            open: true,
            type: "failure",
          },
        })
      );
    }
  };

  useEffect(() => {
    let data: any = sessionStorage.getItem("examDetails");
    data = JSON.parse(data);
    setExamDetails(data);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    getExamRules();
  }, [countryList, pageText]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = () => {
    if (
      pageText?.examTermsConditionsMetadata
        ?.iHaveReadAndAgreeToTheAboveTermsAndConditions &&
      pageText?.examTermsConditionsMetadata?.photoConsentText &&
      acceptTerms.terms &&
      acceptTerms.photoConsent
    ) {
      toggleSteps("candidateData", 3);
      GTM.beginCheckout(examDetails);
    } else if (
      pageText?.examTermsConditionsMetadata
        ?.iHaveReadAndAgreeToTheAboveTermsAndConditions &&
      !pageText?.examTermsConditionsMetadata?.photoConsentText &&
      acceptTerms.terms
    ) {
      toggleSteps("candidateData", 3);
      GTM.beginCheckout(examDetails);
    } else if (
      pageText?.examTermsConditionsMetadata?.photoConsentText &&
      !pageText?.examTermsConditionsMetadata
        ?.iHaveReadAndAgreeToTheAboveTermsAndConditions &&
      acceptTerms.photoConsent
    ) {
      toggleSteps("candidateData", 3);
      GTM.beginCheckout(examDetails);
    } else {
      setOpen(true);
    }
  };

  return (
    <Stack
      mt={11}
      mb={10}
      direction={{ md: "row-reverse" }}
      justifyContent="space-between"
      gap={2}
    >
      <OrderCard examDetails={examDetails} />
      <Paper component={Box} p={{ xs: 2, md: 3 }} sx={styles.termsAndCondition}>
        <div
          dangerouslySetInnerHTML={{
            __html: examRulesData?.standardTerms,
          }}
        ></div>
        <div
          dangerouslySetInnerHTML={{
            __html: examRulesData?.examTerms,
          }}
        ></div>
        <div
          dangerouslySetInnerHTML={{
            __html: examRulesData?.additionalInfo,
          }}
        ></div>
        <Stack sx={styles.conditionsBox} gap={2} py={2}>
          {pageText?.examTermsConditionsMetadata
            ?.iHaveReadAndAgreeToTheAboveTermsAndConditions && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  onChange={(e) =>
                    setAcceptTerms({
                      ...acceptTerms,
                      terms: e.target.checked,
                    })
                  }
                />
              }
              label={
                <Typography fontSize="12px" fontWeight="700">
                  {
                    pageText?.examTermsConditionsMetadata
                      ?.iHaveReadAndAgreeToTheAboveTermsAndConditions
                  }
                </Typography>
              }
            />
          )}
          {pageText?.examTermsConditionsMetadata?.photoConsentText && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  onChange={(e) =>
                    setAcceptTerms({
                      ...acceptTerms,
                      photoConsent: e.target.checked,
                    })
                  }
                />
              }
              label={
                <Typography fontSize="12px" fontWeight="700">
                  {pageText?.examTermsConditionsMetadata?.photoConsentText}
                </Typography>
              }
            />
          )}
        </Stack>
        <Stack direction={{ sm: "row" }} gap={2} py={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIosIcon />}
            onClick={() => toggleSteps("examDetails", 1)}
          >
            {pageText?.buttonsMetadata?.previousStep}
          </Button>
          <Button variant="text" onClick={() => toggleSteps("location", 0)}>
            {pageText?.buttonsMetadata?.cancelButtonText}
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosIcon />}
            onClick={handleSubmit}
            sx={{ ml: { sm: "auto" } }}
          >
            {pageText?.buttonsMetadata?.nextButtonText}
          </Button>
        </Stack>
      </Paper>
      <Popup open={open} handleClose={handleClose}>
        <Box>
          <Box sx={styles.modalBody}>
            {acceptTerms.terms
              ? pageText?.validationMessages?.pleaseAcceptPhotoConsent
              : pageText?.validationMessages?.acceptTermsAndConditions}
          </Box>
          <Stack direction="row" justifyContent="center">
            <Button variant="contained" onClick={handleClose}>
              {pageText?.buttonsMetadata?.okButtonText}
            </Button>
          </Stack>
        </Box>
      </Popup>

      <NoSlotsChecker
        pageText={pageText}
        venueExamGuid={examDetails?.examId}
        toggleSteps={toggleSteps}
      />
    </Stack>
  );
};

export default TermsAndCondition;
